/**
 * CommittedItemsSummary (FLOW-550)
 *
 * What "Commit approved items" leaves behind: every item it just created,
 * scannable, each linking straight to its detail page for edits — no inline
 * editing here.
 *
 * The summary comes from `planCommitStore`, not from the commit response, so
 * this renders identically whether the commit just happened or the user has
 * navigated away and come back. A breakdown committed outside this browser
 * session has no stored summary; the fallback below names the first epic
 * instead — or says nothing about an epic when the proposal created none
 * (FLOW-694).
 */
import { Component, For, Show } from "solid-js";
import { A } from "@solidjs/router";
import { Badge } from "../ui";
import { useProjectPrefix } from "../../contexts/ProjectContext";
import type { InitiativeBreakdown } from "../../api/types";
import type {
  CommittedItem,
  CommittedSummary,
} from "../../stores/planCommitStore";

export interface CommittedItemsSummaryProps {
  breakdown: InitiativeBreakdown;
  summary: CommittedSummary | undefined;
}

/** The first epic the proposal names, or undefined when it names none */
const firstEpicTitle = (breakdown: InitiativeBreakdown): string | undefined => {
  const placement = breakdown.payload.groups[0]?.epic;
  if (!placement) return undefined;
  if ("new" in placement) return placement.new.title;
  if ("existing" in placement) return placement.existing.title;
  return undefined;
};

export const CommittedItemsSummary: Component<CommittedItemsSummaryProps> = (
  props,
) => {
  const prefix = useProjectPrefix();
  const epicTitle = () => firstEpicTitle(props.breakdown);

  return (
    <div class="mt-8 rounded-md border border-signal-green/25 bg-signal-green-tint p-6">
      <p class="text-sm font-medium text-signal-green">Committed</p>

      <Show
        when={props.summary}
        fallback={
          <p class="mt-1 text-sm text-signal-green">
            <Show
              when={epicTitle()}
              fallback={<>Already committed to the backlog.</>}
            >
              {(title) => (
                <Show
                  when={props.breakdown.epic_work_item_id}
                  fallback={
                    <>Created "{title()}" — already committed to the backlog.</>
                  }
                >
                  {(epicId) => (
                    <>
                      Created{" "}
                      <A
                        href={`/items/${epicId()}`}
                        class="font-medium underline hover:text-ink"
                      >
                        {title()}
                      </A>{" "}
                      — already committed to the backlog.
                    </>
                  )}
                </Show>
              )}
            </Show>
          </p>
        }
      >
        {(summary) => (
          <ul class="mt-3 flex flex-col gap-2">
            <For each={summary().epics}>
              {(epic) => (
                <SummaryRow item={epic} type="epic" prefix={prefix()} />
              )}
            </For>
            <For each={summary().stories}>
              {(story) => (
                <SummaryRow item={story} type="story" prefix={prefix()} />
              )}
            </For>
          </ul>
        )}
      </Show>
    </div>
  );
};

const SummaryRow: Component<{
  item: CommittedItem;
  type: "epic" | "story";
  prefix: string;
}> = (props) => (
  <li>
    <A
      href={`/items/${props.item.id}`}
      class="flex items-center gap-3 rounded border border-signal-green/25 bg-surface px-3 py-2 hover:border-signal-green/50"
    >
      <Badge variant={props.type}>
        {props.type === "epic" ? "Epic" : "Story"}
      </Badge>
      <span class="flex-shrink-0 text-xs font-medium text-ink-3 tabular-nums">
        {props.prefix}-{props.item.item_number}
      </span>
      <span class="truncate text-sm text-ink">{props.item.title}</span>
    </A>
  </li>
);

export default CommittedItemsSummary;
